import React, { useCallback, useMemo, useState } from 'react';
import { Classes, Position } from '@blueprintjs/core';
import { FiMoreVertical } from 'react-icons/fi';
import { Button, DropdownMenu, Menu, MenuItem, Tooltip } from 'core/components';
import type { Measurement, Query, QueryResultRow } from '@kentik/ui-shared/query/query.types';
import { buildRowFilters, getRowFilterCandidates } from './rowFilters';
import type { AddRowFilters, RowFilterCandidate } from './rowFilters';

export interface UdeRowActionsProps {
  row: QueryResultRow;
  query: Query;
  measurement: Measurement;
  onAddFilters: AddRowFilters;
}

function itemText(candidate: RowFilterCandidate, label: string) {
  const text = `${label}: ${candidate.displayValue}`;
  return candidate.unfilterableReason ? <Tooltip content={candidate.unfilterableReason}>{text}</Tooltip> : text;
}

function UdeRowActionsMenu({ row, query, measurement, onAddFilters }: UdeRowActionsProps) {
  const [isOpen, setIsOpen] = useState(false);

  const candidates = useMemo(
    () => (isOpen ? getRowFilterCandidates({ query, measurement, row }) : []),
    [isOpen, query, measurement, row]
  );

  const filterable = candidates.filter((candidate) => !candidate.unfilterableReason);

  const apply = useCallback(
    (selected: RowFilterCandidate[], include: boolean) => onAddFilters(buildRowFilters(selected), { include }),
    [onAddFilters]
  );

  const renderSubItems = (include: boolean) =>
    candidates.map((candidate) => (
      <MenuItem
        key={candidate.dimension}
        text={itemText(candidate, candidate.label)}
        disabled={!!candidate.unfilterableReason}
        className={Classes.POPOVER_DISMISS}
        onClick={() => apply([candidate], include)}
      />
    ));

  const hasMultipleDimensions = query.dimensions.length > 1;

  return (
    <DropdownMenu
      isOpen={isOpen}
      onInteraction={setIsOpen}
      position={Position.BOTTOM_RIGHT}
      content={
        <Menu style={{ maxWidth: 400 }}>
          <MenuItem
            icon="th-derived"
            text="Include this in query"
            disabled={!filterable.length}
            className={Classes.POPOVER_DISMISS}
            onClick={() => apply(filterable, true)}
          >
            {hasMultipleDimensions ? renderSubItems(true) : undefined}
          </MenuItem>
          <MenuItem
            icon="exclude-row"
            text="Exclude this in query"
            disabled={!filterable.length}
            className={Classes.POPOVER_DISMISS}
            onClick={() => apply(filterable, false)}
          >
            {hasMultipleDimensions ? renderSubItems(false) : undefined}
          </MenuItem>
        </Menu>
      }
    >
      <Button icon={FiMoreVertical} minimal color="muted" small ariaHaspopup ariaLabel="Row filter actions" />
    </DropdownMenu>
  );
}

export default UdeRowActionsMenu;
